import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getIdToken } from '@helios/auth-firebase-web';
import { usePowerSyncDb } from '../adapters/powersync';
import { getUploadQueueStats } from '../sync';
import {
  dispatchAppointmentBooked,
  dispatchSameDayReminder,
  isRetryable,
  outcomeMessage,
  type AppointmentBookedOutcome,
} from './appointmentBookedClient';

/* ------------------------------------------------------------------ */
/*  PRD.299 — dispatch status surface                                  */
/*                                                                     */
/*  Lives above the router so it survives the booking drawer closing.  */
/*  The drawer fires and forgets; this shows what happened and lets    */
/*  the user cancel while the request is still pending.                */
/*                                                                     */
/*  Cancel disappears the moment the request goes out — a WhatsApp     */
/*  message cannot be recalled, and a Cancel that appears to work      */
/*  after dispatch would be a lie.                                     */
/* ------------------------------------------------------------------ */

const ENDPOINT_URL = import.meta.env.VITE_APPOINTMENT_BOOKED_URL ?? '';
/** PRD.300 — same-day reminder, fired on the same post-create sync. */
const REMINDER_URL = import.meta.env.VITE_APPOINTMENT_REMINDER_URL ?? '';

/** Longest we wait for PowerSync to upload the appointment before giving up. */
const UPLOAD_WAIT_MS = 10_000;
const UPLOAD_POLL_MS = 250;

interface DispatchState {
  appointmentId: string;
  patientName: string;
  phase: 'pending' | 'done';
  outcome: AppointmentBookedOutcome | null;
  /** True for a button press — every outcome then gets an explanation. */
  manual: boolean;
}

interface AppointmentBookedContextValue {
  /** No-op when the feature is unconfigured, so callers need no guard. */
  notifyAppointmentBooked: (
    appointmentId: string,
    patientName: string,
    opts?: { force?: boolean; manual?: boolean },
  ) => void;
  /**
   * PRD.300 path B. INDEPENDENT of the booking confirmation: suppressing the
   * confirmation (toggle off, patient not opted in, endpoint unconfigured)
   * must not also suppress the reminder — they are separate features, and the
   * backend decides reminder eligibility on its own.
   */
  notifyAppointmentReminder: (appointmentId: string) => void;
  isConfigured: boolean;
}

const AppointmentBookedContext = createContext<AppointmentBookedContextValue>({
  notifyAppointmentBooked: () => {},
  notifyAppointmentReminder: () => {},
  isConfigured: false,
});

export function useAppointmentBookedNotifier(): AppointmentBookedContextValue {
  return useContext(AppointmentBookedContext);
}

export function AppointmentBookedStatusProvider({ children }: { children: ReactNode }) {
  const { db } = usePowerSyncDb();
  const [state, setState] = useState<DispatchState | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const postJson = useCallback(async (url: string, body: unknown, signal: AbortSignal) => {
    const token = await getIdToken();
    const res = await fetch(url, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
    let parsed: Record<string, unknown> = {};
    try {
      parsed = (await res.json()) as Record<string, unknown>;
    } catch {
      /* empty body is fine */
    }
    return { status: res.status, body: parsed };
  }, []);

  // Shared by both dispatchers: the backend reads Postgres, so neither may
  // fire before PowerSync has uploaded the appointment.
  const waitForUpload = useCallback(
    async (signal: AbortSignal) => {
      if (!db) return true; // no handle — let the endpoint's own retry decide
      const deadline = Date.now() + UPLOAD_WAIT_MS;
      while (Date.now() < deadline) {
        if (signal.aborted) return false;
        try {
          const pending = (await getUploadQueueStats(db)).count;
          if (pending === 0) {
            return true;
          }
        } catch (err) {
          console.warn('[notifications] upload queue check failed — proceeding', err);
          return true;
        }
        await new Promise((r) => setTimeout(r, UPLOAD_POLL_MS));
      }
      return false;
    },
    [db],
  );

  const run = useCallback(
    async (
      appointmentId: string,
      patientName: string,
      opts?: { force?: boolean; manual?: boolean },
    ) => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      setState({
        appointmentId,
        patientName,
        phase: 'pending',
        outcome: null,
        manual: opts?.manual === true,
      });

      const outcome = await dispatchAppointmentBooked(
        {
          endpointUrl: ENDPOINT_URL,
          waitForUpload,
          postJson,
        },
        appointmentId,
        ctrl.signal,
        { force: opts?.force },
      );

      setState((prev) =>
        prev && prev.appointmentId === appointmentId ? { ...prev, phase: 'done', outcome } : prev,
      );
    },
    [postJson, waitForUpload],
  );

  // PRD.300 path B — fired by the booking form for every created appointment,
  // independently of the confirmation. Deliberately silent: the backend decides
  // eligibility, and "not today", "before 06:00" or "batch already covered it"
  // are the normal outcomes, not something to report to the user.
  const notifyAppointmentReminder = useCallback(
    (appointmentId: string) => {
      if (!REMINDER_URL) {
        console.warn(
          '[reminders] same-day reminder skipped — VITE_APPOINTMENT_REMINDER_URL is not set',
        );
        return;
      }
      const ctrl = new AbortController();
      void dispatchSameDayReminder(
        { postJson, waitForUpload, endpointUrl: REMINDER_URL },
        appointmentId,
        ctrl.signal,
      )
        .then((outcome) => {
          // Only a genuine failure is worth reporting — skips ("not today",
          // "before 06:00", "the batch already covered it") are normal.
          if (outcome.status === 'error') {
            console.warn('[reminders] same-day reminder failed', outcome.message);
          }
        })
        .catch((err) => console.warn('[reminders] same-day reminder threw', err));
    },
    [postJson, waitForUpload],
  );

  const notifyAppointmentBooked = useCallback(
    (appointmentId: string, patientName: string, opts?: { force?: boolean; manual?: boolean }) => {
      if (!ENDPOINT_URL || !db) {
        console.warn(
          '[notifications] booking confirmation skipped — endpoint or PowerSync unavailable',
          { bookedUrlConfigured: Boolean(ENDPOINT_URL), hasPowerSyncDb: Boolean(db) },
        );
        return;
      }
      // Deliberately not awaited — the booking is already saved and must never
      // wait on the confirmation.
      void run(appointmentId, patientName, opts);
    },
    [db, run],
  );

  const value = useMemo(
    () => ({
      notifyAppointmentBooked,
      notifyAppointmentReminder,
      isConfigured: Boolean(ENDPOINT_URL),
    }),
    [notifyAppointmentBooked, notifyAppointmentReminder],
  );

  const message = state?.outcome ? outcomeMessage(state.outcome, { verbose: state.manual }) : null;
  const showChip = state !== null && (state.phase === 'pending' || message !== null);

  return (
    <AppointmentBookedContext.Provider value={value}>
      {children}
      {showChip && state && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--sp-6, 24px)',
            right: 'var(--sp-6, 24px)',
            // Above Drawer and Modal, which are BOTH 9999 (see @helios/ui
            // overlay). At equal z-index the later portal wins, so the chip
            // rendered behind an open booking drawer — invisible exactly when
            // a manual send needs to report back.
            zIndex: 10050,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-3, 12px)',
            maxWidth: 420,
            padding: '10px 14px',
            borderRadius: 10,
            border: '1px solid var(--border, #e2e8f0)',
            background: 'var(--surface, #fff)',
            boxShadow: '0 10px 30px rgba(15,23,42,0.12)',
            fontSize: 14,
          }}
        >
          <span aria-hidden>{state.phase === 'pending' ? '⏳' : chipIcon(state.outcome)}</span>
          <span style={{ flex: 1 }}>
            {state.phase === 'pending'
              ? `Sending "Appointment Booked" to ${state.patientName}…`
              : message}
          </span>

          {state.phase === 'pending' && (
            <button
              type="button"
              onClick={() => {
                abortRef.current?.abort();
                setState((prev) =>
                  prev ? { ...prev, phase: 'done', outcome: { status: 'cancelled' } } : prev,
                );
              }}
              style={chipButtonStyle}
            >
              Cancel
            </button>
          )}

          {state.phase === 'done' && state.outcome && isRetryable(state.outcome) && (
            <button
              type="button"
              onClick={() => void run(state.appointmentId, state.patientName)}
              style={chipButtonStyle}
            >
              Retry
            </button>
          )}

          {state.phase === 'done' && (
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => setState(null)}
              style={{ ...chipButtonStyle, border: 0 }}
            >
              ✕
            </button>
          )}
        </div>
      )}
    </AppointmentBookedContext.Provider>
  );
}

function chipIcon(outcome: AppointmentBookedOutcome | null): string {
  switch (outcome?.status) {
    case 'sent':
    case 'queued':
      return '✓';
    case 'cancelled':
      return '⊘';
    case 'skipped':
      return 'ⓘ';
    default:
      return '⚠';
  }
}

const chipButtonStyle: React.CSSProperties = {
  border: '1px solid var(--border, #e2e8f0)',
  background: 'transparent',
  borderRadius: 6,
  padding: '4px 8px',
  cursor: 'pointer',
  fontSize: 13,
};
